<template>
  <div>
    <div v-if="!loading && startupCosts.length" class="mb-3">
      <v-app>
        <div class="d-flex justify-end">
          <erase-icon-button
            v-if="removeButtonVisible"
            class="mr-2"
            @click="onDelete"
          />
          <business-guide-search v-model="searchValue" />
        </div>
      </v-app>
    </div>

    <ib-list-view-placeholder
      v-if="!loading && !startupCosts.length"
      :disabled="!canEdit || disabled"
      :title="$tc('addStartupCost')"
      @click="onShowAddNewDialog"
    />
    <template v-else>
      <el-row
        v-if="!loading"
        :gutter="20"
        class="d-flex flex-wrap"
      >
        <el-col :md="12">
          <add-new-card
            :disabled="!canEdit"
            class="mb-2 py-md-0"
            :title="$tc('addStartupCost')"
            horizontal
            min-height="62px"
            @click="onShowAddNewDialog"
          />
        </el-col>

        <el-col
          v-for="startupCost in filterStartupCosts"
          :key="startupCost.id"
          class="d-flex mb-2"
          :md="12"
        >
          <div class="d-flex w-100">
            <set-up-and-projections-card
              :disabled="!canEdit"
              class="w-100"
              :entity="startupCost"
              :name="startupCost.name"
              :date="$moment(startupCost.whenWillItOccur).format('MMM YYYY')"
              :amount="getFormattedAmount(startupCost.amount)"
              :frequency="startupCost.oneTime ? $t('oneTime') : $t('installments')"
              :type="startupCost.typeGroup"
              view="list"
              @edit="onEditStartupCost"
              @copy="onCopyStartupCost"
              @delete="onDeleteStartupCost"
            />
          </div>
        </el-col>
      </el-row>
    </template>

    <!-- Dialog Add Startup Cost -->
    <dialog-add-startup-cost
      v-if="canEdit"
      :dialog-add-startup-cost-visible="dialogAddNewVisible"
      :startup-cost-action="startupCostAction"
      @close-dialog-on-button="dialogAddNewVisible = false"
      @close-dialog-add-startup-cost="dialogAddNewVisible = false; startupCostAction = null"
      @on-update-startup-cost-view="onUpdateAddNewDialog()"
    />
    <!-- /Dialog Add Startup Cost -->
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import AddNewCard from '@/components/_v2/AddNewCard.vue'
import BusinessGuideSearch from '@/views/Home/Components/BusinessGuideSearch.vue'
import DialogAddStartupCost from '@/views/Home/StoryMode/SetUp/StartupCosts/Dialogs/DialogAddStartupCost.vue'
import EraseIconButton from '@/components/_v2/EraseIconButton.vue'
import EventBus from '@/event-bus'
import MixinApiCalculations from '@/mixins/apiCalculations'
import MixinCurrencies from '@/mixins/currencies'
import MixinDeleteIdeaExamples from '@/mixins/deleteIdeaExamples'
import MixinIdeaRoles from '@/mixins/permissions/ideaRoles'
import SetUpAndProjectionsCard from '@/views/Home/StoryMode/Components/Cards/SetUpAndProjectionsCard.vue'

export default {
  name: 'StartupCostsList',

  components: {
    EraseIconButton,
    DialogAddStartupCost,
    SetUpAndProjectionsCard,
    AddNewCard,
    BusinessGuideSearch
  },

  mixins: [
    MixinCurrencies,
    MixinApiCalculations,
    MixinIdeaRoles,
    MixinDeleteIdeaExamples
  ],

  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      searchValue: '',
      loading: false,
      startupCostAction: null,
      dialogAddNewVisible: false
    }
  },

  computed: {
    ...mapGetters('idea', {
      startupCosts: 'getStartupCosts'
    }),

    filterStartupCosts () {
      if (this.searchValue !== '' && this.searchValue) {
        return this.startupCosts.filter(item => item.name.toUpperCase().includes(this.searchValue.toUpperCase()))
      }

      return this.startupCosts
    },

    removeButtonVisible () {
      return this.canEdit && this.startupCostHasItemsFromTemplate
    }
  },

  mounted () {
    EventBus.$on('add-edit', () => {
      this.onShowAddNewDialog()
    })

    EventBus.$on('remove-examples', () => {
      this.deleteExamples()
    })

    EventBus.$on('reload', () => {
      this.onUpdateAddNewDialog()
    })
  },

  beforeDestroy () {
    EventBus.$off('add-edit')
    EventBus.$off('remove-examples')
    EventBus.$off('reload')
  },

  methods: {
    ...mapActions('idea', [
      'deleteStoryModeStartupCosts'
    ]),

    onShowAddNewDialog () {
      this.getStartupCosts()
      this.dialogAddNewVisible = true
    },

    onEditStartupCost (startupCost) {
      if (!this.canEdit) return
      this.startupCostAction = Object.assign({}, startupCost)
      this.dialogAddNewVisible = true
    },

    onCopyStartupCost (startupCost) {
      if (!this.canEdit) return
      this.startupCostAction = Object.assign({}, startupCost)
      this.startupCostAction.id = 0
      this.dialogAddNewVisible = true
    },

    onUpdateAddNewDialog () {
      this.emitLoading(true)
      Promise.all([
        this.getStartupCosts(),
        this.getTotalCosts(),
        this.getProfitAndCashFlow()
      ])
        .then(() => {
          this.emitLoading(false)
        })
        .finally(() => {
          this.emitLoading(false)
        })
    },

    async onDeleteStartupCost (startupCost) {
      this.emitLoading(true)
      this.deleteStoryModeStartupCosts(startupCost.id)
        .then(async _ => {
          await this.getStartupCosts()
          await this.getTotalCosts()
          await this.getProfitAndCashFlow()
          this.emitLoading(false)
        }).catch(_ => {
          this.emitLoading(false)
        })
    },

    deleteExamples () {
      this.deleteExamplesMixin('startup-costs')
        .then(_ => { this.onUpdateAddNewDialog() })
    },

    emitLoading (value) {
      this.loading = value
      EventBus.$emit('loading', value)
      this.$emit('loading', value)
    }
  }
}
</script>
